'use client';

import React, { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

export interface HamperShowcaseItem {
  id: string;
  number: string;
  name: string;
  categoryTitle: string;
  overlayTitle1: string;
  overlayTitle2: string;
  headline: string;
  date: string;
  tag: string;
  badge: string;
  shortSnippet: string;
  desc: string;
  heroImage: string;
  portraitImage: string;
  startingPrice: number;
  priceNote: string;
  moq: string;
  inclusionsTitle: string;
  ctaText: string;
  href: string;
  items: string[];
}

// Spring sliding animations for cards
const slideVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.98,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      x: { type: 'spring' as const, stiffness: 280, damping: 28 },
      opacity: { duration: 0.28 },
      scale: { duration: 0.32 },
    },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
    scale: 0.98,
    transition: {
      x: { type: 'spring' as const, stiffness: 280, damping: 28 },
      opacity: { duration: 0.28 },
      scale: { duration: 0.32 },
    },
  }),
};

// Subtle smooth text sliding animations
const textVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 35 : -35,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.32, ease: 'easeOut' },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -35 : 35,
    opacity: 0,
    transition: { duration: 0.2, ease: 'easeIn' },
  }),
};

// Roll sliding animation for outline index number
const numberVariants: Variants = {
  enter: (direction: number) => ({
    y: direction > 0 ? 40 : -40,
    opacity: 0,
  }),
  center: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.28, ease: 'easeOut' },
  },
  exit: (direction: number) => ({
    y: direction > 0 ? -40 : 40,
    opacity: 0,
    transition: { duration: 0.2, ease: 'easeIn' },
  }),
};

export default function FestivalCollections() {
  const { hampers } = useCommerce();

  // Dynamic pricing lookup based on catalog hampers if present
  const dynamicPrices = useMemo(() => {
    let festMin = 499;
    let occMin = 399;
    let corpMin = 650;

    if (hampers && hampers.length > 0) {
      const festItems = hampers.filter(
        (h) =>
          h.moduleType === 'festivals' ||
          ['festivals', 'diwali', 'pongal', 'puthandu', 'navaratri', 'krishna-jayanthi'].includes(h.occasionCategory)
      );
      if (festItems.length > 0) {
        festMin = Math.min(...festItems.map((h) => h.basePrice || 499));
      }

      const occItems = hampers.filter(
        (h) =>
          h.moduleType === 'occasions' ||
          ['weddings', 'housewarming', 'temple', 'birthday', 'return-gifts'].includes(h.occasionCategory)
      );
      if (occItems.length > 0) {
        occMin = Math.min(...occItems.map((h) => h.basePrice || 399));
      }

      const corpItems = hampers.filter(
        (h) =>
          h.moduleType === 'corporate' ||
          ['corporate', 'executive', 'vip', 'corporate-diwali', 'employee-milestone'].includes(h.occasionCategory)
      );
      if (corpItems.length > 0) {
        corpMin = Math.min(...corpItems.map((h) => h.basePrice || 650));
      }
    }

    return { festMin, occMin, corpMin };
  }, [hampers]);

  // The 4 Primary Hamper Category Showcases with tailored landscape hero & portrait preview images
  const hamperCategories: HamperShowcaseItem[] = useMemo(() => [
    {
      id: 'festivals',
      number: '01',
      name: 'Festival Hampers',
      categoryTitle: 'Seasonal & Cultural Celebrations',
      overlayTitle1: 'FESTIVAL',
      overlayTitle2: 'HAMPERS',
      headline: 'Traditional harvest festival, Diwali & sacred celebration trays with authentic native sweets.',
      date: 'Diwali • Pongal • Navaratri • Onam',
      tag: 'Auspicious Festivals',
      badge: 'Festive Special',
      shortSnippet: 'Diwali illumination, Pongal harvest trays, Navaratri prasadam & festive keepsake boxes.',
      desc: 'Commemorating South India’s most auspicious festivals with authentic native jaggery sweets, pure ghee delights, crunchy hand-twisted Kai Murukku, spicy thattai, and handcrafted brassware accompaniment gifts.',
      heroImage: '/images/fest_pongal_toon.jpg',
      portraitImage: '/images/hamper_festival_portrait.jpg',
      startingPrice: dynamicPrices.festMin,
      priceNote: 'box',
      moq: 'Min. 10 Boxes',
      inclusionsTitle: 'Festive Inclusions',
      ctaText: 'Explore Festival Hampers',
      href: '/festivals',
      items: [
        'Pure Ghee Mysore Pak & Sakkarai Pongal Mix (250g)',
        'Chettinad Spicy Thattai & Butter Seedai (200g)',
        'Traditional Hand-Twisted Kai Murukku (200g)',
        'Madras Royal Mixture & Artisanal Brass Diya',
      ],
    },
    {
      id: 'occasions',
      number: '02',
      name: 'Occasional Hampers',
      categoryTitle: 'Weddings, Housewarming & Milestones',
      overlayTitle1: 'OCCASIONAL',
      overlayTitle2: 'HAMPERS',
      headline: 'Regal celebration packages and heirloom sweet assortments for weddings, housewarmings & return gifts.',
      date: 'Weddings • Housewarming • Birthdays',
      tag: 'Heirloom Milestones',
      badge: 'Celebration Favorite',
      shortSnippet: 'Weddings, housewarmings, sacred temple functions & personalized return gift thamboolams.',
      desc: 'Honoring lifelong milestones with bespoke celebration boxes, heirloom family sweet assortments, personalized thamboolam trays, and custom printed gift tags for wedding guests and family reunions.',
      heroImage: '/images/hamper_occasions_landscape.jpg',
      portraitImage: '/images/occasion_wedding_toon.jpg',
      startingPrice: dynamicPrices.occMin,
      priceNote: 'thamboolam pack',
      moq: 'Min. 15 Boxes',
      inclusionsTitle: 'Celebration Inclusions',
      ctaText: 'Explore Occasional Hampers',
      href: '/occasions',
      items: [
        'Artisanal Ribbon-Tied Sweet & Savoury Keepsake Box',
        'Tirunelveli Ghee Halwa & Soft Mysore Pak (250g)',
        'Crispy Ribbon Pakoda & Manapparai Murukku (200g)',
        'Personalized Milestone Gift Card & Guest Tagging',
      ],
    },
    {
      id: 'corporate',
      number: '03',
      name: 'Corporate Hampers',
      categoryTitle: 'Executive & Prestige Business Gifting',
      overlayTitle1: 'CORPORATE',
      overlayTitle2: 'HAMPERS',
      headline: 'Executive festive trunks, client appreciation suites & custom-embossed company logo hampers.',
      date: 'Executive Suites • B2B Pan-India',
      tag: 'Enterprise & B2B',
      badge: 'Corporate Bestseller',
      shortSnippet: 'Executive metallic trunks, branded company lids, multi-desk dispatch & volume B2B tiers.',
      desc: 'Delight valued clients, corporate partners, and employees with premium South Indian gourmet hampers featuring custom metallic logo lid stamping, multi-address pan-India courier dispatch, and GST business billing.',
      heroImage: '/images/hamper_corporate_landscape.jpg',
      portraitImage: '/images/occasion_corporate_toon.jpg',
      startingPrice: dynamicPrices.corpMin,
      priceNote: 'volume tier box',
      moq: 'Min. 25 Boxes',
      inclusionsTitle: 'Executive Inclusions',
      ctaText: 'Explore Corporate Hampers',
      href: '/corporate',
      items: [
        'Metallic Gold / Silver Company Logo Foil Stamping',
        'Premium Velvet-Lined Wooden Chest or Magnetic Trunk',
        'Artisanal Ghee Sweets & Roasted Savouries',
        'Pan-India Multi-Address Direct Desk Dispatch',
      ],
    },
    {
      id: 'custom',
      number: '04',
      name: 'Custom Hampers',
      categoryTitle: 'Interactive 8-Step Box Builder',
      overlayTitle1: 'CUSTOM',
      overlayTitle2: 'HAMPERS',
      headline: 'Interactive configurator to handpick your snacks, box designs, sleeve colors & bespoke branding.',
      date: 'Bespoke Configurator • Real-time Quotes',
      tag: '100% Bespoke',
      badge: 'Interactive Configurator',
      shortSnippet: 'Handpick 30+ native sweets & chips, select box styling, add personal message & get instant quotes.',
      desc: 'Design your one-of-a-kind hamper step by step. Choose carton or rigid box dimensions, handpick favorite wood-fired chips and native delicacies, customize sleeve artwork, and unlock real-time volume pricing.',
      heroImage: '/images/hamper_custom_landscape.jpg',
      portraitImage: '/images/custom_box_carton.jpg',
      startingPrice: 299,
      priceNote: 'custom base pack',
      moq: 'Flexible MOQ',
      inclusionsTitle: 'Configurator Features',
      ctaText: 'Build Custom Hamper',
      href: '/custom-package',
      items: [
        '8-Step Interactive Visual Snack & Box Configurator',
        'Choice of 30+ Authentic Wood-Fired Snacks & Sweets',
        'Custom Sleeve Colors, Ribbon Styles & Personal Cards',
        'Instant Real-Time Automated Volume Pricing Tier',
      ],
    },
  ], [dynamicPrices]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<number>(1);

  const totalCount = hamperCategories.length;

  const handleNext = useCallback(() => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % totalCount);
  }, [totalCount]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + totalCount) % totalCount);
  }, [totalCount]);

  const handleSelect = (idx: number) => {
    if (idx === activeIndex) return;
    setDirection(idx > activeIndex ? 1 : -1);
    setActiveIndex(idx);
  };

  // Compute cards in view for the editorial multi-card sequence
  const prev2Index = (activeIndex - 2 + totalCount) % totalCount;
  const prev1Index = (activeIndex - 1 + totalCount) % totalCount;

  const prev2Item = hamperCategories[prev2Index];
  const prev1Item = hamperCategories[prev1Index];
  const activeItem = hamperCategories[activeIndex];

  return (
    <section id="festivals" className="w-full py-16 md:py-24 bg-[#FFFFFF] border-b border-[#DDDAC4] overflow-hidden">
      {/* Full-width container with generous horizontal breathing room */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-3 mb-12 sm:mb-16">
          <div className="inline-flex items-center space-x-1.5 px-3.5 py-1 rounded-full bg-[#E09F55]/20 text-[#950E09] text-xs font-bold uppercase tracking-widest border border-[#E09F55]/40">
            <Sparkles className="w-3.5 h-3.5 text-[#E09F55]" />
            <span>Curated Hamper Collections • Auspicious Gifting Trays</span>
          </div>
          <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-[#0F382A] tracking-tight leading-[1.18]">
            Artisanal Hampers for Every Auspicious Celebration
          </h2>
          <p className="text-sm sm:text-base text-[#171711]/70 font-sans max-w-2xl mx-auto">
            Explore our signature hamper experiences—from auspicious festival celebrations and heirloom family occasions to prestige corporate suites and bespoke custom boxes.
          </p>
        </div>

        {/* Editorial Awwwards-style Stage Layout expanded to full width */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-12 items-stretch w-full">
          
          {/* Left & Center: Multi-Card Slider & Excerpts (8 cols on desktop) */}
          <div className="lg:col-span-8 flex flex-col justify-between w-full">
            
            {/* Desktop 3-Card Panoramic Strip with full-width scaling */}
            <div className="hidden lg:flex items-stretch gap-3.5 xl:gap-4 h-[400px] xl:h-[440px] 2xl:h-[480px] w-full">
              
              {/* Inactive Card 1 (Far Left Narrow Crop - Uses Portrait Image with Top-Face Framing) */}
              <div 
                onClick={() => {
                  setDirection(-1);
                  setActiveIndex(prev2Index);
                }}
                className="w-20 xl:w-24 2xl:w-28 h-full relative rounded-none overflow-hidden cursor-pointer group shrink-0 border border-neutral-200 transition-all hover:opacity-90 bg-neutral-900"
                title={`Switch to ${prev2Item.name}`}
              >
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                  <motion.div
                    key={prev2Item.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 w-full h-full"
                  >
                    <img 
                      src={prev2Item.portraitImage || prev2Item.heroImage} 
                      alt={prev2Item.name} 
                      className="w-full h-full object-cover object-[center_16%] grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10"></div>
                    <div className="absolute bottom-4 left-3 select-none">
                      <span 
                        className="font-display font-light text-2xl xl:text-3xl text-transparent"
                        style={{ WebkitTextStroke: '1px rgba(255,255,255,0.7)', color: 'transparent' }}
                      >
                        {prev2Item.number}
                      </span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Inactive Card 2 (Middle Left Narrow Crop - Uses Portrait Image with Top-Face Framing) */}
              <div 
                onClick={() => {
                  setDirection(-1);
                  setActiveIndex(prev1Index);
                }}
                className="w-24 xl:w-32 2xl:w-36 h-full relative rounded-none overflow-hidden cursor-pointer group shrink-0 border border-neutral-200 transition-all hover:opacity-90 bg-neutral-900"
                title={`Switch to ${prev1Item.name}`}
              >
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                  <motion.div
                    key={prev1Item.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 w-full h-full"
                  >
                    <img 
                      src={prev1Item.portraitImage || prev1Item.heroImage} 
                      alt={prev1Item.name} 
                      className="w-full h-full object-cover object-[center_16%] grayscale-[18%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10"></div>
                    <div className="absolute bottom-4 left-3.5 select-none">
                      <span 
                        className="font-display font-light text-2xl xl:text-3xl text-transparent"
                        style={{ WebkitTextStroke: '1px rgba(255,255,255,0.7)', color: 'transparent' }}
                      >
                        {prev1Item.number}
                      </span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Active Wide Landscape Hero Card - Perfectly Fits 16:9 Landscape Artwork */}
              <Link 
                href={activeItem.href}
                className="flex-1 h-full relative rounded-none overflow-hidden border border-neutral-200 shadow-md bg-neutral-900 group cursor-pointer block"
                title={`Click to view ${activeItem.name} page`}
              >
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                  <motion.div
                    key={activeItem.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 w-full h-full"
                  >
                    <img 
                      src={activeItem.heroImage} 
                      alt={activeItem.name} 
                      className="w-full h-full object-cover object-[center_20%] group-hover:scale-103 transition-transform duration-700 ease-out" 
                    />
                    
                    {/* Gentle gradient overlay for contrast without washing out image */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent pointer-events-none" />

                    {/* Top right interactive floating pill */}
                    <div className="absolute top-4 right-4 z-10">
                      <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur-md text-[#0F382A] text-[11px] font-bold shadow-md tracking-wider uppercase group-hover:bg-[#950E09] group-hover:text-white transition-all duration-300">
                        <span>{activeItem.name}</span>
                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>

                    {/* Bottom overlay title */}
                    <div className="absolute bottom-4 left-5 z-10 transition-transform duration-300 group-hover:-translate-y-1">
                      <span 
                        className="font-display font-light text-2xl xl:text-3xl text-white drop-shadow-md block tracking-wide"
                      >
                        {activeItem.overlayTitle1} <span className="font-semibold text-[#F2BC6F]">{activeItem.overlayTitle2}</span>
                      </span>
                      <span className="text-[11px] font-mono text-neutral-200 flex items-center space-x-1.5 mt-0.5">
                        <span>Click to explore {activeItem.name.toLowerCase()} page</span>
                        <ArrowRight className="w-3.5 h-3.5 text-[#F2BC6F]" />
                      </span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </Link>

            </div>

            {/* Mobile / Tablet Single Hero Card - Clickable with Page Redirection & Perfectly Positioned Headspace */}
            <Link
              href={activeItem.href}
              className="block lg:hidden relative aspect-[16/10] sm:aspect-[16/9] w-full rounded-none overflow-hidden border border-neutral-200 shadow-md bg-neutral-900 group cursor-pointer"
              title={`Click to explore ${activeItem.name}`}
            >
              <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.div
                  key={activeItem.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="absolute inset-0 w-full h-full"
                >
                  <img 
                    src={activeItem.heroImage} 
                    alt={activeItem.name} 
                    className="w-full h-full object-cover object-[center_20%]" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/10 pointer-events-none" />
                  <div className="absolute bottom-4 left-4 z-10">
                    <span className="font-display font-semibold text-lg text-white drop-shadow-md block">
                      {activeItem.name}
                    </span>
                    <span className="text-[11px] font-mono text-[#F2BC6F] flex items-center space-x-1.5 mt-0.5">
                      <span>Tap to explore page</span>
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </Link>

            {/* Desktop Excerpts Row below cards with smooth directional sliding */}
            <div className="hidden lg:flex items-start gap-3.5 xl:gap-4 pt-4 mt-2 border-t border-neutral-100">
              
              {/* Under Inactive Card 1 */}
              <div 
                onClick={() => {
                  setDirection(-1);
                  setActiveIndex(prev2Index);
                }}
                className="w-20 xl:w-24 2xl:w-28 shrink-0 cursor-pointer group"
              >
                <span className="font-mono text-xs font-bold text-neutral-400 block group-hover:text-black transition-colors">
                  {prev2Item.number}
                </span>
                <p className="text-[11px] text-neutral-500 font-sans leading-relaxed line-clamp-2 mt-1 group-hover:text-neutral-700 transition-colors">
                  {prev2Item.name}
                </p>
              </div>

              {/* Under Inactive Card 2 */}
              <div 
                onClick={() => {
                  setDirection(-1);
                  setActiveIndex(prev1Index);
                }}
                className="w-24 xl:w-32 2xl:w-36 shrink-0 cursor-pointer group"
              >
                <span className="font-mono text-xs font-bold text-neutral-400 block group-hover:text-black transition-colors">
                  {prev1Item.number}
                </span>
                <p className="text-[11px] text-neutral-500 font-sans leading-relaxed line-clamp-2 mt-1 group-hover:text-neutral-700 transition-colors">
                  {prev1Item.name}
                </p>
              </div>

              {/* Under Active Card: Detailed Excerpt + Page Redirection Link */}
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                  key={activeItem.id + '-excerpt'}
                  custom={direction}
                  variants={textVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="flex-1 flex flex-col justify-between space-y-3 pl-2"
                >
                  <p className="text-xs xl:text-sm text-neutral-600 font-sans leading-relaxed line-clamp-3">
                    {activeItem.desc}
                  </p>

                  <div className="flex items-center justify-between pt-1">
                    <div className="font-mono text-xs text-[#950E09] font-bold">
                      STARTING FROM ₹{activeItem.startingPrice} <span className="text-[10px] text-neutral-400 font-normal">/ {activeItem.priceNote}</span>
                    </div>

                    <Link
                      href={activeItem.href}
                      className="inline-flex items-center space-x-2 text-xs font-bold uppercase tracking-widest text-[#0F382A] hover:text-[#950E09] group whitespace-nowrap cursor-pointer transition-colors"
                    >
                      <span>{activeItem.ctaText}</span>
                      <span className="w-10 h-[1px] bg-current transition-all group-hover:w-16"></span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>

            </div>

          </div>

          {/* Right Column: Editorial Index, Inclusions & Controls (4 cols on desktop) */}
          <div className="lg:col-span-4 flex flex-col justify-between h-full pt-1 lg:pl-6 xl:pl-8 border-t lg:border-t-0 lg:border-l border-neutral-200">
            
            <div>
              {/* Outline Number with Leading Hairline Rule */}
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-14 sm:w-20 h-[1px] bg-neutral-300"></div>
                <div className="h-16 overflow-hidden flex items-center">
                  <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    <motion.span
                      key={activeItem.number}
                      custom={direction}
                      variants={numberVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="font-display font-light text-5xl sm:text-6xl text-transparent select-none tracking-tight block"
                      style={{
                        WebkitTextStroke: '1.5px #171711',
                        color: 'transparent',
                      }}
                    >
                      {activeItem.number}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#950E09] font-bold">
                  {activeItem.tag}
                </div>
              </div>

              {/* Editorial Headline with sliding animation */}
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                  key={activeItem.id + '-details'}
                  custom={direction}
                  variants={textVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  <h3 className="font-display font-semibold text-xl sm:text-2xl text-[#171711] leading-tight mb-3">
                    {activeItem.headline}
                  </h3>
                  
                  <p className="text-xs sm:text-sm text-neutral-600 font-sans leading-relaxed mb-5">
                    {activeItem.desc}
                  </p>

                  {/* Highlights / Inclusions Box */}
                  <div className="p-4 rounded-xl bg-[#F7F4D6]/50 border border-[#DDDAC4] mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold text-[#950E09] uppercase tracking-wider block">
                        {activeItem.inclusionsTitle}
                      </span>
                      <span className="text-[11px] font-bold text-[#0F382A]">
                        {activeItem.moq}
                      </span>
                    </div>
                    <ul className="space-y-1.5 text-xs text-[#171711] font-sans">
                      {activeItem.items.map((it, idx) => (
                        <li key={idx} className="flex items-center space-x-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#387613] shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Direct Redirection CTA in Details Panel */}
                  <div className="mb-6">
                    <Link
                      href={activeItem.href}
                      className="w-full py-2.5 px-4 rounded-lg bg-[#0F382A] hover:bg-[#950E09] text-white text-xs font-bold shadow-sm transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <span>{activeItem.ctaText}</span>
                      <ArrowRight className="w-4 h-4 text-[#F2BC6F] transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Number Selectors: Vertical stack on desktop, horizontal row on mobile */}
              <div className="mb-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-2">
                  Select Hamper Category
                </div>
                
                {/* Desktop vertical stack */}
                <div className="hidden lg:flex flex-col space-y-1.5">
                  {hamperCategories.map((cat, idx) => (
                    <div
                      key={cat.id}
                      onClick={() => handleSelect(idx)}
                      className={`group flex items-center justify-between py-1.5 px-2.5 rounded-md transition-all cursor-pointer ${
                        idx === activeIndex
                          ? 'bg-[#0F382A]/5 text-[#0F382A]'
                          : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2.5">
                        <span className={`font-mono text-xs font-bold ${idx === activeIndex ? 'text-[#0F382A] underline decoration-2 underline-offset-4' : 'text-neutral-400'}`}>
                          {cat.number}
                        </span>
                        <span className={`text-xs font-sans ${idx === activeIndex ? 'text-neutral-900 font-bold' : 'text-neutral-600'}`}>
                          {cat.name}
                        </span>
                      </div>
                      
                      <Link
                        href={cat.href}
                        onClick={(e) => e.stopPropagation()}
                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded flex items-center gap-1 transition-all ${
                          idx === activeIndex
                            ? 'bg-[#0F382A] text-[#F7F4D6] hover:bg-[#950E09]'
                            : 'text-neutral-400 opacity-60 group-hover:opacity-100 hover:text-black hover:bg-neutral-200'
                        }`}
                        title={`Go to ${cat.name} page`}
                      >
                        <span>View Page</span>
                        <ArrowRight className="w-2.5 h-2.5" />
                      </Link>
                    </div>
                  ))}
                </div>

                {/* Mobile horizontal pills */}
                <div className="flex lg:hidden items-center space-x-2 overflow-x-auto pb-2">
                  {hamperCategories.map((cat, idx) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSelect(idx)}
                      className={`px-3 py-1.5 text-xs font-mono rounded border transition-all cursor-pointer whitespace-nowrap ${
                        idx === activeIndex
                          ? 'border-[#0F382A] bg-[#0F382A] text-white font-bold shadow-xs'
                          : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'
                      }`}
                    >
                      {cat.number} • {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile inline Explore Page button */}
            <div className="block lg:hidden mb-6">
              <Link
                href={activeItem.href}
                className="w-full py-3 px-4 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] text-xs font-bold shadow-md shadow-[#950E09]/20 transition-all flex items-center justify-center space-x-2 cursor-pointer"
              >
                <span>{activeItem.ctaText}</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#F2BC6F]" />
              </Link>
            </div>

            {/* Bottom Controls: Side-by-side rectangular outline boxes */}
            <div className="pt-2">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePrev}
                  aria-label="Previous Category"
                  className="flex-1 py-3.5 px-4 border border-neutral-300 hover:border-black hover:bg-neutral-50 active:bg-neutral-100 transition-all flex items-center justify-between group cursor-pointer shadow-sm hover:shadow"
                >
                  <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 group-hover:text-black">
                    — PREV —
                  </span>
                  <span className="text-sm font-serif font-bold text-neutral-700 group-hover:text-black transition-transform group-hover:-translate-x-1">
                    «
                  </span>
                </button>

                <button
                  onClick={handleNext}
                  aria-label="Next Category"
                  className="flex-1 py-3.5 px-4 border border-neutral-300 hover:border-black hover:bg-neutral-50 active:bg-neutral-100 transition-all flex items-center justify-between group cursor-pointer shadow-sm hover:shadow"
                >
                  <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 group-hover:text-black">
                    — NEXT —
                  </span>
                  <span className="text-sm font-serif font-bold text-neutral-700 group-hover:text-black transition-transform group-hover:translate-x-1">
                    »
                  </span>
                </button>
              </div>

              {/* Scroll / Page Indicator */}
              <div className="flex items-center justify-end space-x-2 text-[10px] tracking-widest uppercase text-neutral-400 font-mono mt-3">
                <span>SCROLL</span>
                <div className="w-8 h-[1px] bg-neutral-300"></div>
                <span className="text-neutral-700 font-bold">{activeItem.number} / 04</span>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
